import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers';
import FilterCard from '../../components/filter-card';
import DataTable from '../../components/data-table';
import Table from '../../components/table';
import Pagination from '../../components/pagination';
import RoleBasedAccess from '../../components/role-based-access';
import useI18n from '../../i18n/useI18n';
import general from '../../utils/general';
import { UserTypeEnum } from '../../utils/enums';
import DateScenarioModal from './DateScenarioModal';
import DateScenarioDetailModal from './DateScenarioDetailModal';
import PreferenceDateCard from '../../components/preference-date-card';
import './DateScenarioPage.scss';

const filterDefaults = {
  cityId: '',
  universityId: '',
  programId: '',
  scope: 'all',
  academicYear: '',
  examType: '',
  isActive: '',
  includeDeleted: '',
};

const toDateTimeLabel = (value, locale = 'tr-TR') => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const toDateLabel = (value, locale = 'tr-TR') => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const toInputDateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const iso = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
  return iso;
};

const toInputDateOnlyValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const mapApiList = (data) => {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const DateScenarioPage = ({
  titleKey,
  subtitleKey,
  entityNameKey,
  emptyStateKey = 'DATE_SCENARIO_EMPTY_STATE',
  addButtonKey = 'DATE_SCENARIO_ADD_BUTTON',
  scenarioType = 'generic',
  endpoints,
  headerTabs = null,
  usageInfoKey = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [filters, setFilters] = useState({ ...filterDefaults });
  const [universities, setUniversities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programLookup, setProgramLookup] = useState({});
  const [modalPrograms, setModalPrograms] = useState([]);
  const [modalProgramsLoading, setModalProgramsLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [modalInitialValues, setModalInitialValues] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [simplePagination, setSimplePagination] = useState({ page: 1, limit: 10 });
  const [serverPagination, setServerPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const isExamScenario = scenarioType === 'exam';
  const isRegistrationScenario = scenarioType === 'registrationDates';
  const isPreferenceDatesScenario = scenarioType === 'preferenceDates';
  const isPreferenceGuidesScenario = scenarioType === 'preferenceGuides';
  const usesSimpleTable =
    isExamScenario || isRegistrationScenario || isPreferenceDatesScenario || isPreferenceGuidesScenario;
  const isSimplePaginationEnabled =
    isRegistrationScenario || isPreferenceDatesScenario || isPreferenceGuidesScenario;
  const supportsServerPagination = isExamScenario;
  const isDetailModalOpen = !!detailRecord;
  const requiresProgramSelection = isExamScenario;
  const shouldLoadPrograms = !usesSimpleTable || requiresProgramSelection;

  // Global (all-universities) endpoint map — only used for initial load
  const globalApiUrl = useMemo(() => {
    if (isRegistrationScenario) return 'universities/global/registration-dates/all-universities';
    if (isPreferenceDatesScenario) return 'universities/global/preference-dates/all-universities';
    if (isPreferenceGuidesScenario) return 'universities/global/preference-guides/all-universities';
    return null;
  }, [isPreferenceDatesScenario, isPreferenceGuidesScenario, isRegistrationScenario]);

  const sanitizeFilters = (incomingFilters, previousFilters = filters) => {
    const nextFilters = { ...incomingFilters };
    if (String(previousFilters?.cityId || '') !== String(nextFilters.cityId || '')) {
      nextFilters.universityId = '';
      nextFilters.programId = '';
    }
    if (String(previousFilters?.universityId || '') !== String(nextFilters.universityId || '')) {
      nextFilters.programId = '';
    }
    return nextFilters;
  };

  const createEndpointParams = (overrides = {}) => {
    const normalizedFilters = overrides.filters || filters;
    return {
      universityId:
        overrides.universityId !== undefined
          ? overrides.universityId
          : normalizedFilters.universityId || '',
      cityId: overrides.cityId !== undefined ? overrides.cityId : normalizedFilters.cityId || '',
      programId:
        overrides.programId !== undefined ? overrides.programId : normalizedFilters.programId || '',
      recordId: overrides.recordId || null,
      scenarioType,
      filters: normalizedFilters,
      page: overrides.page,
      limit: overrides.limit,
    };
  };

  const normalizeEndpointConfig = (config) => {
    if (!config) return null;
    if (typeof config === 'string') {
      if (!config) return null;
      return { url: config };
    }
    if (typeof config === 'object' && config.url) {
      return {
        url: config.url,
        query: config.query,
      };
    }
    return null;
  };

  const getEndpointConfig = (factory, overrides = {}) => {
    if (typeof factory !== 'function') return null;
    const params = createEndpointParams(overrides);
    try {
      const result = factory(params);
      return normalizeEndpointConfig(result);
    } catch (error) {
      return null;
    }
  };

  const selectedUniversity = useMemo(
    () => universities.find((uni) => String(uni.id) === String(filters.universityId)),
    [filters.universityId, universities]
  );

  const programOptions = useMemo(() => {
    return programs
      .map((program) => {
        const programId = program.id ?? program.programId ?? program.value;
        if (programId === undefined || programId === null) {
          return null;
        }
        return {
          value: String(programId),
          label: program.name ?? program.title ?? program.label ?? '-',
        };
      })
      .filter(Boolean);
  }, [programs]);

  const programMap = useMemo(() => {
    const map = new Map();
    programs.forEach((program) => {
      const programId = program.id ?? program.programId ?? program.value;
      if (programId !== undefined && programId !== null) {
        map.set(String(programId), program.name ?? program.title ?? program.label ?? '-');
      }
    });
    return map;
  }, [programs]);

  const universityMap = useMemo(() => {
    const map = new Map();
    universities.forEach((university) => {
      const universityId = university.id ?? university.universityId ?? university.value;
      if (universityId !== undefined && universityId !== null) {
        map.set(String(universityId), university.name ?? university.title ?? university.label ?? '-');
      }
    });
    return map;
  }, [universities]);

  const examTypeOptions = useMemo(
    () => [
      { value: '', label: t('COMMON_ALL') },
      { value: 'yos', label: t('EXAM_DATES_EXAM_TYPE_YOS') },
      { value: 'sat', label: t('EXAM_DATES_EXAM_TYPE_SAT') },
      { value: 'toefl', label: t('EXAM_DATES_EXAM_TYPE_TOEFL') },
      { value: 'ielts', label: t('EXAM_DATES_EXAM_TYPE_IELTS') },
      { value: 'other', label: t('EXAM_DATES_EXAM_TYPE_OTHER') },
    ],
    [t]
  );
  const examTypeLabelLookup = useMemo(
    () => ({
      yos: t('EXAM_DATES_EXAM_TYPE_YOS'),
      sat: t('EXAM_DATES_EXAM_TYPE_SAT'),
      toefl: t('EXAM_DATES_EXAM_TYPE_TOEFL'),
      ielts: t('EXAM_DATES_EXAM_TYPE_IELTS'),
      other: t('EXAM_DATES_EXAM_TYPE_OTHER'),
    }),
    [t]
  );

  const scopeLabel = (scope) => {
    if (scope === 'program') {
      return t('DATE_SCENARIO_SCOPE_PROGRAM');
    }
    if (scope === 'university') {
      return t('DATE_SCENARIO_SCOPE_UNIVERSITY');
    }
    return t('COMMON_ALL');
  };

  const decoratedRecords = useMemo(() => {
    const buildPeriodLabel = (start, end) => {
      if (start && end) {
        return `${toDateLabel(start)} - ${toDateLabel(end)}`;
      }
      if (start) {
        return toDateLabel(start);
      }
      return '-';
    };

    if (isExamScenario) {
      return records.map((record) => {
        const id = record.id ?? record._id ?? record.dateId ?? record.date_id;
        const academicYear = record.academicYear ?? record.year ?? record.academic_year;
        const programId =
          record.programId ??
          record.program_id ??
          record.program?.id ??
          record.program?.programId ??
          filters.programId ??
          '';
        const universityId =
          record.universityId ??
          record.university_id ??
          record.university?.id ??
          record.university?.universityId ??
          filters.universityId ??
          '';
        const examTypeRaw = record.examType ?? record.exam_type ?? '';
        const examType = examTypeRaw ? String(examTypeRaw).toLowerCase() : '';
        const examDate = record.examDate ?? record.exam_date ?? record.date;
        const registrationDeadline =
          record.registrationDeadline ?? record.registration_deadline ?? record.registrationDate;
        const resultAnnouncementDate =
          record.resultAnnouncementDate ??
          record.result_announcement_date ??
          record.resultAnnouncement ??
          record.resultDate;
        const isActive =
          record.isActive ?? record.is_active ?? record.isPublished ?? record.is_published ?? true;
        const createdAt = record.createdAt ?? record.created_at;
        const updatedAt = record.updatedAt ?? record.updated_at;

        return {
          id,
          title:
            record.title ||
            record.name ||
            `${programMap.get(String(programId)) || t('DATE_SCENARIO_ENTITY_EXAM')} - ${academicYear || '-'
            }`,
          programId: programId ? String(programId) : '',
          programName: programId ? programMap.get(String(programId)) ?? record.program?.name ?? '-' : '-',
          universityId: universityId ? String(universityId) : '',
          academicYear: academicYear ? academicYear.toString() : '-',
          examType,
          examTypeLabel: examType ? examTypeLabelLookup[examType] || examType.toUpperCase() : '-',
          examDate,
          examDateLabel: toDateTimeLabel(examDate),
          registrationDeadline,
          registrationDeadlineLabel: registrationDeadline ? toDateTimeLabel(registrationDeadline) : '-',
          resultAnnouncementDate,
          resultAnnouncementDateLabel: resultAnnouncementDate
            ? toDateTimeLabel(resultAnnouncementDate)
            : '-',
          notes: record.notes ?? record.note ?? record.description ?? '',
          isActive: !!isActive,
          statusLabel: isActive
            ? t('DATE_SCENARIO_STATUS_ACTIVE')
            : t('DATE_SCENARIO_STATUS_INACTIVE'),
          createdAtLabel: toDateTimeLabel(createdAt),
          updatedAtLabel: toDateTimeLabel(updatedAt),
          universityLogo:
            record.university?.logoUrl ??
            record.program?.university?.logoUrl ??
            record.university_logo ??
            '',
          raw: record,
        };
      });
    }

    if (isRegistrationScenario) {
      return records.map((record) => {
        const id = record.id ?? record._id ?? record.dateId ?? record.date_id;
        const academicYear = record.academicYear ?? record.year ?? record.academic_year;
        const programId =
          record.programId ??
          record.program_id ??
          record.program?.id ??
          record.program?.programId ??
          filters.programId ??
          '';
        const universityId =
          record.universityId ??
          record.university_id ??
          record.university?.id ??
          record.university?.universityId ??
          filters.universityId ??
          '';
        const normalizedProgramId = programId ? String(programId) : '';
        const normalizedUniversityId = universityId ? String(universityId) : '';
        const buildPeriod = (index) => {
          const start = record[`period${index}StartDate`] ?? record[`period${index}_start_date`];
          const end = record[`period${index}EndDate`] ?? record[`period${index}_end_date`];
          return {
            start,
            end,
            label: buildPeriodLabel(start, end),
          };
        };
        const createdAt = record.createdAt ?? record.created_at;
        const updatedAt = record.updatedAt ?? record.updated_at;
        return {
          id,
          programId: normalizedProgramId,
          universityId: normalizedUniversityId,
          universityName:
            normalizedUniversityId
              ? universityMap.get(normalizedUniversityId) ??
              record.university?.name ??
              record.university?.title ??
              '-'
              : '-',
          academicYear: academicYear ? academicYear.toString() : '-',
          period1: buildPeriod(1),
          period2: buildPeriod(2),
          period3: buildPeriod(3),
          notes: record.notes ?? record.note ?? record.description ?? '',
          createdAtLabel: toDateTimeLabel(createdAt),
          updatedAtLabel: toDateTimeLabel(updatedAt),
          raw: record,
        };
      });
    }

    if (isPreferenceDatesScenario) {
      const buildPeriod = (record, index) => {
        const start = record[`period${index}StartDate`] ?? record[`period${index}_start_date`];
        const end = record[`period${index}EndDate`] ?? record[`period${index}_end_date`];
        const announcement =
          record[`period${index}AnnouncementDate`] ?? record[`period${index}_announcement_date`];
        return {
          start,
          end,
          announcement,
          label: buildPeriodLabel(start, end),
          announcementLabel: announcement ? toDateLabel(announcement) : '-',
        };
      };

      return records.map((record) => {
        const id = record.id ?? record._id ?? record.dateId ?? record.date_id;
        const academicYear = record.academicYear ?? record.year ?? record.academic_year;
        const universityId =
          record.universityId ??
          record.university_id ??
          record.university?.id ??
          record.university?.universityId ??
          filters.universityId ??
          '';
        const normalizedUniversityId = universityId ? String(universityId) : '';

        return {
          id,
          universityId: normalizedUniversityId,
          universityName:
            normalizedUniversityId
              ? universityMap.get(normalizedUniversityId) ??
              record.university?.name ??
              record.university?.title ??
              '-'
              : '-',
          academicYear: academicYear ? academicYear.toString() : '-',
          period1: buildPeriod(record, 1),
          period2: buildPeriod(record, 2),
          period3: buildPeriod(record, 3),
          notes: record.notes ?? record.note ?? record.description ?? '',
          raw: record,
        };
      });
    }

    if (isPreferenceGuidesScenario) {
      return records.map((record) => {
        const id = record.id ?? record._id ?? record.guideId ?? record.guide_id;
        const academicYear = record.academicYear ?? record.year ?? record.academic_year;
        const universityId =
          record.universityId ??
          record.university_id ??
          record.university?.id ??
          record.university?.universityId ??
          filters.universityId ??
          '';
        const normalizedUniversityId = universityId ? String(universityId) : '';
        const guideType = record.guideType ?? record.guide_type ?? 'preference_guide';
        const fileUrl = record.fileUrl ?? record.file_url ?? '';
        const externalUrl = record.externalUrl ?? record.external_url ?? record.url ?? '';
        const isActive =
          record.isActive ?? record.is_active ?? record.isPublished ?? record.is_published ?? true;

        return {
          id,
          universityId: normalizedUniversityId,
          universityName:
            normalizedUniversityId
              ? universityMap.get(normalizedUniversityId) ??
              record.university?.name ??
              record.university?.title ??
              '-'
              : '-',
          academicYear: academicYear ? academicYear.toString() : '-',
          title: record.title || record.name || '-',
          guideType,
          guideTypeLabel:
            guideType === 'preference_guide'
              ? t('DATE_SCENARIO_GUIDE_TYPE_PREFERENCE_GUIDE')
              : guideType || '-',
          fileUrl,
          externalUrl,
          description: record.description ?? record.note ?? '',
          isActive: !!isActive,
          statusLabel: isActive
            ? t('DATE_SCENARIO_STATUS_ACTIVE')
            : t('DATE_SCENARIO_STATUS_INACTIVE'),
          raw: record,
        };
      });
    }

    return records.map((record) => {
      const id = record.id ?? record._id ?? record.dateId ?? record.date_id;
      const scope =
        record.scope ||
        record.scopeType ||
        record.scope_type ||
        (record.programId || record.program_id ? 'program' : 'university');
      const programId =
        record.programId ??
        record.program_id ??
        record.program?.id ??
        record.program?.programId ??
        null;
      const universityId =
        record.universityId ??
        record.university_id ??
        record.university?.id ??
        record.university?.universityId ??
        filters.universityId ??
        '';
      const startDate =
        record.startDate ?? record.start_date ?? record.beginDate ?? record.begin_date;
      const endDate = record.endDate ?? record.end_date ?? record.finishDate ?? record.finish_date;
      const status =
        record.isActive ??
        record.is_active ??
        record.isPublished ??
        record.is_published ??
        record.status ??
        true;

      return {
        id,
        raw: record,
        title: record.title || record.name || record.label || '-',
        scope,
        scopeLabel: scopeLabel(scope),
        programId: programId ? String(programId) : '',
        programName: programId ? programMap.get(String(programId)) ?? record.program?.name ?? '-' : '-',
        universityId: universityId ? String(universityId) : '',
        startDate,
        startDateLabel: toDateTimeLabel(startDate),
        endDate,
        endDateLabel: toDateTimeLabel(endDate),
        note: record.note ?? record.description ?? record.details ?? '',
        url: record.url ?? record.link ?? record.registrationUrl ?? '',
        isActive: !!status,
        statusLabel: status
          ? t('DATE_SCENARIO_STATUS_ACTIVE')
          : t('DATE_SCENARIO_STATUS_INACTIVE'),
      };
    });
  }, [
    filters.universityId,
    isExamScenario,
    isPreferenceDatesScenario,
    isPreferenceGuidesScenario,
    isRegistrationScenario,
    programMap,
    universityMap,
    records,
    t,
  ]);

  const filteredRecords = useMemo(() => {
    if (usesSimpleTable) {
      return decoratedRecords;
    }
    let output = [...decoratedRecords];
    if (filters.scope !== 'all') {
      output = output.filter((item) => item.scope === filters.scope);
    }
    if (filters.programId) {
      output = output.filter(
        (item) => item.programId && item.programId.toString() === filters.programId.toString()
      );
    }
    return output;
  }, [decoratedRecords, filters.programId, filters.scope, usesSimpleTable]);

  const simplePaginationMeta = useMemo(() => {
    if (!usesSimpleTable || !isSimplePaginationEnabled) {
      return null;
    }
    const totalItems = filteredRecords.length;
    const totalPages = totalItems > 0 ? Math.ceil(totalItems / simplePagination.limit) : 1;
    const currentPage = Math.min(simplePagination.page, totalPages) || 1;
    return {
      totalItems,
      totalPages,
      currentPage,
    };
  }, [
    filteredRecords.length,
    isSimplePaginationEnabled,
    simplePagination.limit,
    simplePagination.page,
    usesSimpleTable,
  ]);

  const simpleTableData = useMemo(() => {
    if (!usesSimpleTable) {
      return filteredRecords;
    }
    if (!isSimplePaginationEnabled || !simplePaginationMeta) {
      return filteredRecords;
    }
    const startIndex = (simplePaginationMeta.currentPage - 1) * simplePagination.limit;
    const endIndex = startIndex + simplePagination.limit;
    return filteredRecords.slice(startIndex, endIndex);
  }, [
    filteredRecords,
    isSimplePaginationEnabled,
    simplePagination.limit,
    simplePaginationMeta,
    usesSimpleTable,
  ]);

  useEffect(() => {
    if (!isSimplePaginationEnabled) {
      return;
    }
    setSimplePagination((prev) => ({ ...prev, page: 1 }));
  }, [filters.universityId, isSimplePaginationEnabled]);

  useEffect(() => {
    if (!simplePaginationMeta) {
      return;
    }
    if (simplePagination.page !== simplePaginationMeta.currentPage) {
      setSimplePagination((prev) => ({ ...prev, page: simplePaginationMeta.currentPage }));
    }
  }, [simplePagination.page, simplePaginationMeta]);

  const handleSimplePageChange = (page) => {
    if (!isSimplePaginationEnabled || page === simplePagination.page) {
      return;
    }
    setSimplePagination((prev) => ({ ...prev, page }));
  };

  const handleSimpleLimitChange = (limit) => {
    if (!isSimplePaginationEnabled || !limit || limit === simplePagination.limit) {
      return;
    }
    setSimplePagination({ page: 1, limit });
  };

  const handleServerPageChange = (page) => {
    if (!supportsServerPagination || !filters.universityId) {
      return;
    }
    if (requiresProgramSelection && !filters.programId) {
      return;
    }
    if (page === serverPagination.page) {
      return;
    }
    setServerPagination((prev) => ({
      ...prev,
      page,
    }));
    fetchRecords({
      universityId: filters.universityId,
      programId: filters.programId,
      page,
      limit: serverPagination.limit,
    });
  };

  const handleServerLimitChange = (limit) => {
    if (!supportsServerPagination || !filters.universityId) {
      return;
    }
    if (requiresProgramSelection && !filters.programId) {
      return;
    }
    if (!limit || limit === serverPagination.limit) {
      return;
    }
    setServerPagination({
      page: 1,
      limit,
      total: serverPagination.total,
      totalPages: serverPagination.totalPages,
    });
    fetchRecords({
      universityId: filters.universityId,
      programId: filters.programId,
      page: 1,
      limit,
    });
  };

  const fetchUniversities = () => {
    const baseQuery = { limit: 99, is_active: true };
    const loadPage = (page = 1, acc = []) => {
      dispatch(
        ApiCallActions.Get({
          url: 'universities',
          query: { ...baseQuery, page },
          showAlertOnError: false,
          onSuccess: ({ data }) => {
            const list = mapApiList(data);
            const combined = [...acc, ...list];
            const meta = data?.meta || {};
            const totalPages =
              meta.totalPages ?? meta.total_pages ?? meta.totalpages ?? 1;
            if (page < totalPages) {
              loadPage(page + 1, combined);
              return;
            }
            setUniversities(combined);
          },
          onError: () => {
            setUniversities(acc);
          },
        })
      );
    };
    setUniversities([]);
    loadPage(1, []);
  };

  const fetchPrograms = (universityId) => {
    if (!universityId) {
      setPrograms([]);
      return;
    }

    setProgramsLoading(true);
    dispatch(
      ApiCallActions.Get({
        url: `universities/${universityId}/programs`,
        showAlertOnError: false,
        onSuccess: ({ data }) => {
          const list = mapApiList(data);
          setPrograms(list);
          setProgramLookup((prev) => ({
            ...prev,
            [universityId]: list,
          }));
          setProgramsLoading(false);
        },
        onError: () => {
          setPrograms([]);
          setProgramsLoading(false);
        },
      })
    );
  };

  const loadProgramsForModal = (universityId) => {
    if (isRegistrationScenario || isPreferenceDatesScenario || isPreferenceGuidesScenario) {
      setModalPrograms([]);
      return Promise.resolve([]);
    }
    if (!universityId) {
      setModalPrograms([]);
      return Promise.resolve([]);
    }

    const cached = programLookup[universityId];
    if (cached) {
      setModalPrograms(cached);
      return Promise.resolve(cached);
    }

    setModalProgramsLoading(true);
    return new Promise((resolve) => {
      dispatch(
        ApiCallActions.Get({
          url: `universities/${universityId}/programs`,
          showAlertOnError: false,
          onSuccess: ({ data }) => {
            const list = mapApiList(data);
            setProgramLookup((prev) => ({
              ...prev,
              [universityId]: list,
            }));
            setModalPrograms(list);
            setModalProgramsLoading(false);
            resolve(list);
          },
          onError: () => {
            setModalPrograms([]);
            setModalProgramsLoading(false);
            resolve([]);
          },
        })
      );
    });
  };

  const fetchRecords = ({ universityId, programId, filters: customFilters, page, limit } = {}) => {
    const effectiveFilters = customFilters || filters;
    const targetUniversityId = universityId || effectiveFilters.universityId;
    const targetProgramId = programId || effectiveFilters.programId;

    if (!targetUniversityId && !effectiveFilters.cityId && (requiresProgramSelection && !targetProgramId)) {
      setRecords([]);
      if (supportsServerPagination) {
        setServerPagination((prev) => ({
          ...prev,
          total: 0,
          totalPages: 1,
          page: 1,
        }));
      }
      return;
    }

    const resolvedPage = supportsServerPagination ? page ?? serverPagination.page ?? 1 : undefined;
    const resolvedLimit = supportsServerPagination ? limit ?? serverPagination.limit ?? 10 : undefined;

    const endpointConfig = getEndpointConfig(endpoints.list, {
      universityId: targetUniversityId,
      programId: targetProgramId,
      filters: effectiveFilters,
      page: resolvedPage,
      limit: resolvedLimit,
    });

    if (!endpointConfig?.url) {
      setRecords([]);
      if (supportsServerPagination) {
        setServerPagination((prev) => ({
          ...prev,
          total: 0,
          totalPages: 1,
          page: 1,
        }));
      }
      return;
    }

    setLoading(true);
    dispatch(
      ApiCallActions.Get({
        url: endpointConfig.url,
        query: endpointConfig.query,
        showAlertOnError: true,
        onSuccess: ({ data }) => {
          const list = mapApiList(data);
          setRecords(list);
          if (supportsServerPagination) {
            const meta = data?.meta || {};
            setServerPagination((prev) => {
              const nextLimit = meta.limit ?? resolvedLimit ?? prev.limit ?? 10;
              const nextTotal = meta.total ?? prev.total ?? list.length ?? 0;
              const nextPage = meta.page ?? resolvedPage ?? prev.page ?? 1;
              const inferredTotalPages =
                nextLimit > 0 ? Math.max(1, Math.ceil(nextTotal / nextLimit)) : 1;
              return {
                total: nextTotal,
                page: nextPage,
                limit: nextLimit,
                totalPages: meta.totalPages ?? inferredTotalPages,
              };
            });
          }
          setLoading(false);
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage);
          }
          setRecords([]);
          if (supportsServerPagination) {
            setServerPagination((prev) => ({
              ...prev,
              total: 0,
              totalPages: 1,
              page: 1,
            }));
          }
          setLoading(false);
        },
      })
    );
  };

  useEffect(() => {
    fetchUniversities();
  }, []);

  // İlk açılışta veya filtre temizlendiğinde global API ile listeyi dolu getir
  const fetchGlobalRecords = () => {
    if (!globalApiUrl) return;
    setLoading(true);
    dispatch(
      ApiCallActions.Get({
        url: globalApiUrl,
        showAlertOnError: false,
        onSuccess: ({ data }) => {
          const list = mapApiList(data);
          setRecords(list);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      })
    );
  };

  useEffect(() => {
    fetchGlobalRecords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalApiUrl]);


  const programEffectKey = requiresProgramSelection ? filters.programId : 'all';
  const autoSubmitFieldNames = requiresProgramSelection
    ? ['universityId', 'programId', 'cityId']
    : ['universityId', 'cityId'];

  useEffect(() => {
    if (!filters.universityId && !filters.cityId) {
      setPrograms([]);
      // Global API ile ilk yükleme yapılmışsa filtre boşken kayıtları temizleme, tekrar getir
      if (!globalApiUrl) {
        setRecords([]);
      } else {
        fetchGlobalRecords();
      }
      if (supportsServerPagination) {
        setServerPagination((prev) => ({
          ...prev,
          total: 0,
          totalPages: 1,
          page: 1,
        }));
      }
      return;
    }

    if (shouldLoadPrograms) {
      fetchPrograms(filters.universityId);
    } else {
      setPrograms([]);
    }

    if (requiresProgramSelection && !filters.programId) {
      setRecords([]);
      if (supportsServerPagination) {
        setServerPagination((prev) => ({
          ...prev,
          total: 0,
          totalPages: 1,
          page: 1,
        }));
      }
      return;
    }

    if (supportsServerPagination && serverPagination.page !== 1) {
      setServerPagination((prev) => ({
        ...prev,
        page: 1,
      }));
    }

    fetchRecords({
      universityId: filters.universityId,
      cityId: filters.cityId,
      programId: filters.programId,
      page: supportsServerPagination ? 1 : undefined,
      limit: supportsServerPagination ? serverPagination.limit : undefined,
    });
  }, [
    filters.cityId,
    filters.universityId,
    programEffectKey,
    requiresProgramSelection,
    shouldLoadPrograms,
    supportsServerPagination,
  ]);

  const handleFilterChange = (nextFilters) => {
    setFilters((prev) => sanitizeFilters(nextFilters, prev));
  };

  const handleFilterSubmit = (nextFilters) => {
    const normalizedFilters = sanitizeFilters(nextFilters, filters);
    setFilters(normalizedFilters);
    if (
      (normalizedFilters?.universityId || normalizedFilters?.cityId) &&
      (!requiresProgramSelection || normalizedFilters?.programId)
    ) {
      fetchRecords({
        universityId: normalizedFilters.universityId,
        programId: normalizedFilters.programId,
        cityId: normalizedFilters.cityId,
        filters: normalizedFilters,
        page: supportsServerPagination ? 1 : undefined,
        limit: supportsServerPagination ? serverPagination.limit : undefined,
      });
    } else {
      setRecords([]);
    }
  };

  const handleClearFilters = () => {
    setFilters({ ...filterDefaults });
    setRecords([]);
    if (supportsServerPagination) {
      setServerPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
      });
    }
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    const initialUniversityId = filters.universityId ? String(filters.universityId) : '';
    const initialProgramId = filters.programId ? String(filters.programId) : '';
    const baseValues = {
      title: '',
      scopeType: filters.scope === 'program' ? 'program' : 'university',
      programId: '',
      universityId: initialUniversityId,
      startDate: '',
      endDate: '',
      note: '',
      url: '',
      isActive: true,
      academicYear: '',
      period1StartDate: '',
      period1EndDate: '',
      period1AnnouncementDate: '',
      period2StartDate: '',
      period2EndDate: '',
      period2AnnouncementDate: '',
      period3StartDate: '',
      period3EndDate: '',
      period3AnnouncementDate: '',
      guideType: 'preference_guide',
      fileUrl: '',
      externalUrl: '',
      description: '',
      examType: '',
      examDate: '',
      registrationDeadline: '',
      resultAnnouncementDate: '',
    };

    if (isPreferenceGuidesScenario) {
      setModalInitialValues({
        ...baseValues,
        scopeType: 'university',
      });
      setModalPrograms([]);
    } else if (isPreferenceDatesScenario || isRegistrationScenario) {
      setModalInitialValues({
        ...baseValues,
        scopeType: 'university',
        programId: initialProgramId,
      });
      setModalPrograms([]);
    } else if (isExamScenario) {
      setModalInitialValues({
        ...baseValues,
        scopeType: 'university',
        programId: initialProgramId,
      });
      if (initialUniversityId) {
        loadProgramsForModal(initialUniversityId);
      } else {
        setModalPrograms([]);
      }
    } else {
      setModalInitialValues(baseValues);
      if (initialUniversityId) {
        loadProgramsForModal(initialUniversityId);
      } else {
        setModalPrograms([]);
      }
    }

    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    if (isPreferenceGuidesScenario) {
      setModalInitialValues({
        universityId: record.universityId || filters.universityId || '',
        academicYear: record.academicYear || '',
        guideType: record.guideType || 'preference_guide',
        title: record.title || '',
        fileUrl: record.fileUrl || '',
        externalUrl: record.externalUrl || '',
        description: record.description || '',
        isActive: record.isActive ?? record.is_active ?? true,
      });
      setModalPrograms([]);
    } else if (isPreferenceDatesScenario) {
      setModalInitialValues({
        universityId: record.universityId || filters.universityId || '',
        academicYear: record.academicYear || '',
        period1StartDate: toInputDateOnlyValue(record.period1?.start || record.period1StartDate),
        period1EndDate: toInputDateOnlyValue(record.period1?.end || record.period1EndDate),
        period1AnnouncementDate: toInputDateOnlyValue(
          record.period1?.announcement || record.period1AnnouncementDate
        ),
        period2StartDate: toInputDateOnlyValue(record.period2?.start || record.period2StartDate),
        period2EndDate: toInputDateOnlyValue(record.period2?.end || record.period2EndDate),
        period2AnnouncementDate: toInputDateOnlyValue(
          record.period2?.announcement || record.period2AnnouncementDate
        ),
        period3StartDate: toInputDateOnlyValue(record.period3?.start || record.period3StartDate),
        period3EndDate: toInputDateOnlyValue(record.period3?.end || record.period3EndDate),
        period3AnnouncementDate: toInputDateOnlyValue(
          record.period3?.announcement || record.period3AnnouncementDate
        ),
        note: record.notes || record.note || '',
      });
      setModalPrograms([]);
    } else if (isExamScenario) {
      const rawExamDate = record.examDate || record.raw?.examDate || record.raw?.exam_date || '';
      const rawRegistrationDeadline =
        record.registrationDeadline ||
        record.raw?.registrationDeadline ||
        record.raw?.registration_deadline ||
        '';
      const rawResultAnnouncement =
        record.resultAnnouncementDate ||
        record.raw?.resultAnnouncementDate ||
        record.raw?.result_announcement_date ||
        '';
      setModalInitialValues({
        universityId: record.universityId || filters.universityId || '',
        programId: record.programId || filters.programId || '',
        academicYear: record.academicYear || '',
        examType: record.examType || record.raw?.examType || '',
        examDate: toInputDateOnlyValue(rawExamDate),
        registrationDeadline: toInputDateOnlyValue(rawRegistrationDeadline),
        resultAnnouncementDate: toInputDateOnlyValue(rawResultAnnouncement),
        note: record.notes || record.note || '',
        isActive: record.isActive,
      });
      const targetUniversityId = record.universityId || filters.universityId || '';
      if (targetUniversityId) {
        loadProgramsForModal(targetUniversityId);
      } else {
        setModalPrograms([]);
      }
    } else if (isRegistrationScenario) {
      setModalInitialValues({
        universityId: record.universityId || filters.universityId || '',
        programId: record.programId || filters.programId || '',
        academicYear: record.academicYear || '',
        period1StartDate: toInputDateOnlyValue(record.period1?.start || record.period1StartDate),
        period1EndDate: toInputDateOnlyValue(record.period1?.end || record.period1EndDate),
        period2StartDate: toInputDateOnlyValue(record.period2?.start || record.period2StartDate),
        period2EndDate: toInputDateOnlyValue(record.period2?.end || record.period2EndDate),
        period3StartDate: toInputDateOnlyValue(record.period3?.start || record.period3StartDate),
        period3EndDate: toInputDateOnlyValue(record.period3?.end || record.period3EndDate),
        note: record.notes || record.note || '',
      });
      setModalPrograms([]);
    } else {
      setModalInitialValues({
        title: record.title || '',
        scopeType: record.scope || 'university',
        programId: record.programId || '',
        universityId: record.universityId || filters.universityId || '',
        startDate: toInputDateValue(record.startDate),
        endDate: toInputDateValue(record.endDate),
        note: record.note || '',
        url: record.url || '',
        isActive: record.isActive,
      });
      const targetUniversityId = record.universityId || filters.universityId || '';
      if (targetUniversityId) {
        const cached = programLookup[targetUniversityId];
        if (cached) {
          setModalPrograms(cached);
        } else if (
          filters.universityId &&
          String(filters.universityId) === String(targetUniversityId)
        ) {
          setModalPrograms(programs);
        } else {
          loadProgramsForModal(targetUniversityId);
        }
      } else {
        setModalPrograms([]);
      }
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    setModalInitialValues(null);
    setModalPrograms([]);
  };

  const handleModalUniversityChange = (universityId) => {
    if (!universityId) {
      setModalPrograms([]);
      return;
    }
    if (isRegistrationScenario || isPreferenceDatesScenario || isPreferenceGuidesScenario) {
      return;
    }
    loadProgramsForModal(universityId);
  };

  const openDetailModal = (record) => {
    setDetailRecord(record);
  };

  const closeDetailModal = () => {
    setDetailRecord(null);
  };

  const buildPayloadFromForm = (formValues, options = {}) => {
    const normalizeDateInput = (value) => (value ? value : null);
    const resolveProgramPayload = (target) => {
      if (!target) return {};
      const numericId = Number(target);
      const resolvedId = Number.isNaN(numericId) ? target : numericId;
      return {
        programId: resolvedId,
        program_id: resolvedId,
      };
    };

    if (isExamScenario) {
      return {
        academicYear: formValues.academicYear ? parseInt(formValues.academicYear, 10) : undefined,
        examType: formValues.examType,
        examDate: normalizeDateInput(formValues.examDate),
        registrationDeadline: normalizeDateInput(formValues.registrationDeadline),
        resultAnnouncementDate: normalizeDateInput(formValues.resultAnnouncementDate),
        notes: formValues.note?.trim() || undefined,
        isActive: !!formValues.isActive,
      };
    }

    if (isRegistrationScenario) {
      return {
        academicYear: formValues.academicYear ? parseInt(formValues.academicYear, 10) : undefined,
        period1StartDate: normalizeDateInput(formValues.period1StartDate),
        period1EndDate: normalizeDateInput(formValues.period1EndDate),
        period2StartDate: normalizeDateInput(formValues.period2StartDate),
        period2EndDate: normalizeDateInput(formValues.period2EndDate),
        period3StartDate: normalizeDateInput(formValues.period3StartDate),
        period3EndDate: normalizeDateInput(formValues.period3EndDate),
        notes: formValues.note?.trim() || undefined,
      };
    }

    if (isPreferenceDatesScenario) {
      return {
        academicYear: formValues.academicYear ? parseInt(formValues.academicYear, 10) : undefined,
        period1StartDate: normalizeDateInput(formValues.period1StartDate),
        period1EndDate: normalizeDateInput(formValues.period1EndDate),
        period1AnnouncementDate: normalizeDateInput(formValues.period1AnnouncementDate),
        period2StartDate: normalizeDateInput(formValues.period2StartDate),
        period2EndDate: normalizeDateInput(formValues.period2EndDate),
        period2AnnouncementDate: normalizeDateInput(formValues.period2AnnouncementDate),
        period3StartDate: normalizeDateInput(formValues.period3StartDate),
        period3EndDate: normalizeDateInput(formValues.period3EndDate),
        period3AnnouncementDate: normalizeDateInput(formValues.period3AnnouncementDate),
        notes: formValues.note?.trim() || undefined,
      };
    }

    if (isPreferenceGuidesScenario) {
      return {
        academicYear: formValues.academicYear ? parseInt(formValues.academicYear, 10) : undefined,
        guideType: formValues.guideType,
        title: formValues.title?.trim(),
        fileUrl: formValues.fileUrl?.trim(),
        externalUrl: formValues.externalUrl?.trim() || undefined,
        description: formValues.description?.trim() || undefined,
        isActive: !!formValues.isActive,
      };
    }

    const toIso = (value) => {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toISOString();
    };

    const payload = {
      title: formValues.title?.trim(),
      name: formValues.title?.trim(),
      scope: formValues.scopeType,
      scope_type: formValues.scopeType,
      startDate: toIso(formValues.startDate),
      start_date: toIso(formValues.startDate),
      endDate: toIso(formValues.endDate),
      end_date: toIso(formValues.endDate),
      note: formValues.note?.trim() || undefined,
      description: formValues.note?.trim() || undefined,
      url: formValues.url?.trim() || undefined,
      link: formValues.url?.trim() || undefined,
      isActive: formValues.isActive,
      is_active: formValues.isActive,
      isPublished: formValues.isActive,
      is_published: formValues.isActive,
    };

    if (formValues.scopeType === 'program' && formValues.programId) {
      Object.assign(payload, resolveProgramPayload(formValues.programId));
    } else {
      payload.programId = null;
      payload.program_id = null;
    }

    return payload;
  };

  const handleModalSubmit = (formValues) => {
    const targetUniversityId = formValues.universityId || filters.universityId;
    if (!targetUniversityId) {
      general.notificationError(t('DATE_SCENARIO_MODAL_VALIDATION_UNIVERSITY'));
      return Promise.reject(new Error(t('DATE_SCENARIO_MODAL_VALIDATION_UNIVERSITY')));
    }

    const resolvedUniversityId = String(targetUniversityId);
    const targetProgramId =
      formValues.programId || filters.programId || editingRecord?.programId || '';

    if (requiresProgramSelection && !targetProgramId) {
      general.notificationError(t('DATE_SCENARIO_SELECT_PROGRAM'));
      return Promise.reject(new Error(t('DATE_SCENARIO_SELECT_PROGRAM')));
    }

    const resolvedProgramId = targetProgramId ? String(targetProgramId) : '';
    const payload = buildPayloadFromForm(formValues, { programId: resolvedProgramId });
    const actionCreator = editingRecord ? ApiCallActions.Put : ApiCallActions.Post;
    const endpointConfig = getEndpointConfig(
      editingRecord ? endpoints.update : endpoints.create,
      {
        universityId: resolvedUniversityId,
        programId: resolvedProgramId,
        recordId: editingRecord?.id,
      }
    );

    if (!endpointConfig?.url) {
      general.notificationError(t('DATE_SCENARIO_ENDPOINT_MISSING'));
      return Promise.reject(new Error('missing_endpoint'));
    }

    return new Promise((resolve, reject) => {
      dispatch(
        actionCreator({
          url: endpointConfig.url,
          data: payload,
          showAlertOnError: true,
          showLoading: true,
          onSuccess: () => {
            general.messageSuccess(
              t(editingRecord ? 'DATE_SCENARIO_UPDATE_SUCCESS' : 'DATE_SCENARIO_CREATE_SUCCESS')
            );
            closeModal();
            const shouldUpdateUniversity = String(filters.universityId || '') !== resolvedUniversityId;
            const shouldUpdateProgram =
              requiresProgramSelection &&
              resolvedProgramId &&
              String(filters.programId || '') !== resolvedProgramId;

            if (shouldUpdateUniversity || shouldUpdateProgram) {
              setFilters((prev) => ({
                ...prev,
                universityId: resolvedUniversityId,
                programId: requiresProgramSelection ? resolvedProgramId : prev.programId,
              }));
            }

            if (shouldUpdateUniversity) {
              fetchPrograms(resolvedUniversityId);
            }
            fetchRecords({
              universityId: resolvedUniversityId,
              programId: requiresProgramSelection ? resolvedProgramId : undefined,
              page: supportsServerPagination ? serverPagination.page : undefined,
              limit: supportsServerPagination ? serverPagination.limit : undefined,
            });
            resolve(true);
          },
          onError: ({ errorMessage }) => {
            if (errorMessage) {
              general.notificationError(errorMessage);
            }
            reject(new Error(errorMessage || 'error'));
          },
        })
      );
    });
  };

  const handleDelete = async (record) => {
    const confirmed = await general.confirmDelete(t(entityNameKey), record.title || '-');
    if (!confirmed) return;

    const targetProgramId = record.programId || filters.programId || '';
    if (requiresProgramSelection && !targetProgramId) {
      general.notificationError(t('DATE_SCENARIO_SELECT_PROGRAM'));
      return;
    }

    const endpointConfig = getEndpointConfig(endpoints.delete, {
      universityId: filters.universityId,
      programId: targetProgramId,
      recordId: record.id,
    });

    if (!endpointConfig?.url) {
      general.notificationError(t('DATE_SCENARIO_ENDPOINT_MISSING'));
      return;
    }

    dispatch(
      ApiCallActions.Delete({
        url: endpointConfig.url,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          general.showDeleteSuccess(t(entityNameKey));
          fetchRecords({
            universityId: filters.universityId,
            programId: requiresProgramSelection ? targetProgramId : undefined,
            page: supportsServerPagination ? serverPagination.page : undefined,
            limit: supportsServerPagination ? serverPagination.limit : undefined,
          });
        },
      })
    );
  };

  let tableColumns;
  if (isExamScenario) {
    tableColumns = [
      {
        key: 'universityLogo',
        label: t('DATE_SCENARIO_TABLE_COLUMN_LOGO'),
        render: (value) =>
          value ? (
            <img
              src={value}
              alt="University Logo"
              style={{
                height: '40px',
                width: '40px',
                objectFit: 'contain',
                borderRadius: '4px',
              }}
            />
          ) : (
            '-'
          ),
      },
      {
        key: 'academicYear',
        label: t('DATE_SCENARIO_TABLE_COLUMN_ACADEMIC_YEAR'),
      },
      {
        key: 'programName',
        label: t('EXAM_DATES_TABLE_COLUMN_PROGRAM'),
      },
      {
        key: 'examTypeLabel',
        label: t('EXAM_DATES_TABLE_COLUMN_EXAM_TYPE'),
      },
      {
        key: 'examDateLabel',
        label: t('EXAM_DATES_TABLE_COLUMN_EXAM_DATE'),
      },
      {
        key: 'registrationDeadlineLabel',
        label: t('EXAM_DATES_TABLE_COLUMN_REGISTRATION_DEADLINE'),
      },
      {
        key: 'resultAnnouncementDateLabel',
        label: t('EXAM_DATES_TABLE_COLUMN_RESULT_ANNOUNCEMENT'),
      },
      {
        key: 'statusLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_STATUS'),
      },
    ];
  } else if (isRegistrationScenario) {
    tableColumns = [
      {
        key: 'universityName',
        label: t('DATE_SCENARIO_TABLE_COLUMN_UNIVERSITY'),
      },
      {
        key: 'academicYear',
        label: t('DATE_SCENARIO_TABLE_COLUMN_ACADEMIC_YEAR'),
      },
      // {
      //   key: 'period1',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD1'),
      //   render: (value) => value?.label || '-',
      // },
      // {
      //   key: 'period2',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD2'),
      //   render: (value) => value?.label || '-',
      // },
      // {
      //   key: 'period3',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD3'),
      //   render: (value) => value?.label || '-',
      // },
    ];
  } else if (isPreferenceDatesScenario) {
    tableColumns = [
      {
        key: 'universityName',
        label: t('DATE_SCENARIO_TABLE_COLUMN_UNIVERSITY'),
      },
      {
        key: 'academicYear',
        label: t('DATE_SCENARIO_TABLE_COLUMN_ACADEMIC_YEAR'),
        sortable: true,
      },
      // {
      //   key: 'period1',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD1'),
      //   render: (value) => value?.label || '-',
      // },
      // {
      //   key: 'period1Announcement',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD1_ANNOUNCEMENT'),
      //   render: (_, row) => row.period1?.announcementLabel || '-',
      // },
      // {
      //   key: 'period2',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD2'),
      //   render: (value) => value?.label || '-',
      // },
      // {
      //   key: 'period2Announcement',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD2_ANNOUNCEMENT'),
      //   render: (_, row) => row.period2?.announcementLabel || '-',
      // },
      // {
      //   key: 'period3',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD3'),
      //   render: (value) => value?.label || '-',
      // },
      // {
      //   key: 'period3Announcement',
      //   label: t('DATE_SCENARIO_TABLE_COLUMN_PERIOD3_ANNOUNCEMENT'),
      //   render: (_, row) => row.period3?.announcementLabel || '-',
      // },
      //   {
      //     key: 'notes',
      //     label: t('DATE_SCENARIO_TABLE_COLUMN_NOTE'),
      //     render: (value) => value || '-',
      //   },
    ];
  } else if (isPreferenceGuidesScenario) {
    tableColumns = [
      {
        key: 'title',
        label: t('DATE_SCENARIO_TABLE_COLUMN_TITLE'),
      },
      {
        key: 'universityName',
        label: t('DATE_SCENARIO_TABLE_COLUMN_UNIVERSITY'),
      },
      {
        key: 'academicYear',
        label: t('DATE_SCENARIO_TABLE_COLUMN_ACADEMIC_YEAR'),
      },
      {
        key: 'guideTypeLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_GUIDE_TYPE'),
      },
      {
        key: 'fileUrl',
        label: t('DATE_SCENARIO_TABLE_COLUMN_FILE_URL'),
        render: (value) =>
          value ? (
            <a href={value} target="_blank" rel="noopener noreferrer">
              {t('DATE_SCENARIO_TABLE_ACTION_OPEN_LINK')}
            </a>
          ) : (
            '-'
          ),
      },
      {
        key: 'externalUrl',
        label: t('DATE_SCENARIO_TABLE_COLUMN_EXTERNAL_URL'),
        render: (value) =>
          value ? (
            <a href={value} target="_blank" rel="noopener noreferrer">
              {t('DATE_SCENARIO_TABLE_ACTION_OPEN_LINK')}
            </a>
          ) : (
            '-'
          ),
      },
      //   {
      //     key: 'description',
      //     label: t('DATE_SCENARIO_TABLE_COLUMN_DESCRIPTION'),
      //     render: (value) => value || '-',
      //   },
      {
        key: 'statusLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_STATUS'),
      },
    ];
  } else {
    tableColumns = [
      {
        key: 'title',
        label: t('DATE_SCENARIO_TABLE_COLUMN_TITLE'),
      },
      {
        key: 'scopeLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_SCOPE'),
      },
      {
        key: 'programName',
        label: t('DATE_SCENARIO_TABLE_COLUMN_PROGRAM'),
      },
      {
        key: 'startDateLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_START'),
      },
      {
        key: 'endDateLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_END'),
      },
      //   {
      //     key: 'note',
      //     label: t('DATE_SCENARIO_TABLE_COLUMN_NOTE'),
      //     render: (value) => value || '-',
      //   },
      {
        key: 'statusLabel',
        label: t('DATE_SCENARIO_TABLE_COLUMN_STATUS'),
      },
      {
        key: 'actions',
        label: '',
        render: (_, row) => (
          <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN]}>
            <div className="d-flex">
              <button
                type="button"
                className="btn btn-sm btn-warning btn-icon btn-round m-r-5"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(row);
                }}
                title={t('DATE_SCENARIO_TABLE_ACTION_EDIT')}
              >
                <i className="zmdi zmdi-edit"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger btn-icon btn-round"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row);
                }}
                title={t('DATE_SCENARIO_TABLE_ACTION_DELETE')}
              >
                <i className="zmdi zmdi-delete"></i>
              </button>
            </div>
          </RoleBasedAccess>
        ),
      },
    ];
  }

  const filterFields = useMemo(() => {
    const fields = [];

    if (isRegistrationScenario || isPreferenceDatesScenario || isPreferenceGuidesScenario) {
      fields.push({
        type: 'select',
        name: 'cityId',
        placeholder: t('UNIVERSITIES_FILTERS_CITY_PLACEHOLDER') || 'City',
        fetchUrl: 'cities',
        mapOption: (item) => {
          if (!item) return null;
          const id = item.id ?? item.value;
          const label = item.cityName || item.name || item.label;
          if (id === undefined || id === null || !label) {
            return null;
          }
          return {
            value: String(id),
            label: String(label),
          };
        },
        valueKey: 'id',
        labelKey: 'name',
        icon: 'zmdi-city',
      });
    }

    const filteredUniversities = filters.cityId
      ? universities.filter((uni) => {
        const uniCityId = uni.cityId ?? uni.city?.id ?? uni.city_id;
        return String(uniCityId) === String(filters.cityId);
      })
      : universities;

    fields.push({
      type: 'select',
      name: 'universityId',
      placeholder: t('DATE_SCENARIO_FILTER_UNIVERSITY'),
      options: filteredUniversities.map((uni) => ({
        value: String(uni.id),
        label: uni.name,
      })),
      icon: 'zmdi-city',
    });

    if (!usesSimpleTable || requiresProgramSelection) {
      fields.push({
        type: 'select',
        name: 'programId',
        placeholder: t('DATE_SCENARIO_FILTER_PROGRAM'),
        options: [
          { value: '', label: t('COMMON_ALL') },
          ...programOptions,
        ],
        icon: 'zmdi-graduation-cap',
        disabled: !filters.universityId || programsLoading,
      });
    }

    if (!usesSimpleTable) {
      fields.push({
        type: 'select',
        name: 'scope',
        placeholder: t('DATE_SCENARIO_FILTER_SCOPE'),
        options: [
          { value: 'all', label: t('COMMON_ALL') },
          { value: 'university', label: t('DATE_SCENARIO_SCOPE_UNIVERSITY') },
          { value: 'program', label: t('DATE_SCENARIO_SCOPE_PROGRAM') },
        ],
        icon: 'zmdi-filter-list',
      });
    }

    if (isExamScenario) {
      fields.push(
        {
          type: 'input',
          name: 'academicYear',
          placeholder: t('EXAM_DATES_FILTER_ACADEMIC_YEAR'),
          icon: 'zmdi-calendar',
          inputType: 'number',
        },
        {
          type: 'select',
          name: 'examType',
          placeholder: t('EXAM_DATES_FILTER_EXAM_TYPE'),
          options: examTypeOptions,
          icon: 'zmdi-label',
        },
        {
          type: 'select',
          name: 'isActive',
          placeholder: t('EXAM_DATES_FILTER_STATUS'),
          options: [
            { value: '', label: t('COMMON_ALL') },
            { value: 'true', label: t('DATE_SCENARIO_STATUS_ACTIVE') },
            { value: 'false', label: t('DATE_SCENARIO_STATUS_INACTIVE') },
          ],
          icon: 'zmdi-check-circle',
          roleBased: true,
          roleAccess: [UserTypeEnum.ADMIN],
        },
        {
          type: 'select',
          name: 'includeDeleted',
          placeholder: t('EXAM_DATES_FILTER_INCLUDE_DELETED'),
          options: [
            { value: '', label: t('COMMON_ALL') },
            { value: 'true', label: t('COMMON_YES') },
            { value: 'false', label: t('COMMON_NO') },
          ],
          icon: 'zmdi-delete',
          roleBased: true,
          roleAccess: [UserTypeEnum.ADMIN],
        }
      );
    }

    return fields;
  }, [
    examTypeOptions,
    filters.cityId,
    filters.universityId,
    isExamScenario,
    isPreferenceDatesScenario,
    isPreferenceGuidesScenario,
    isRegistrationScenario,
    programOptions,
    programsLoading,
    requiresProgramSelection,
    t,
    universities,
    usesSimpleTable,
  ]);

  const showViewButton =
    isExamScenario || isRegistrationScenario || isPreferenceDatesScenario || isPreferenceGuidesScenario;

  const renderSimpleTableActions = (row) => (
    <div className="d-flex">
      {showViewButton && (
        <button
          type="button"
          className="btn btn-sm btn-info btn-icon btn-round m-r-5"
          onClick={(e) => {
            e.stopPropagation();
            openDetailModal(row);
          }}
          title={t('DATE_SCENARIO_TABLE_ACTION_VIEW')}
        >
          <i className="zmdi zmdi-eye"></i>
        </button>
      )}
      <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN]}>
        <div className="d-flex">
          <button
            type="button"
            className="btn btn-sm btn-warning btn-icon btn-round m-r-5"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(row);
            }}
            title={t('DATE_SCENARIO_TABLE_ACTION_EDIT')}
          >
            <i className="zmdi zmdi-edit"></i>
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger btn-icon btn-round"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            title={t('DATE_SCENARIO_TABLE_ACTION_DELETE')}
          >
            <i className="zmdi zmdi-delete"></i>
          </button>
        </div>
      </RoleBasedAccess>
    </div>
  );

  const renderHeaderTabs = (options = {}) => {
    if (!headerTabs?.items?.length) {
      return null;
    }
    const { variant = 'default' } = options;
    const containerClass = ['date-scenario-tabs'];
    if (variant === 'card') {
      containerClass.push('date-scenario-tabs--card');
    }
    return (
      <div className={containerClass.join(' ')}>
        <ul className="nav nav-tabs">
          {headerTabs.items.map((tab) => {
            const isActive = headerTabs.activeKey === tab.key;
            return (
              <li className="nav-item" key={tab.key}>
                <a
                  href="#!"
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  onClick={(event) => {
                    event.preventDefault();
                    if (!isActive && typeof headerTabs.onSelect === 'function') {
                      headerTabs.onSelect(tab.key);
                    }
                  }}
                >
                  {tab.label}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {t(titleKey)}
              <small>{t(subtitleKey)}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN]}>
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                type="button"
                onClick={openCreateModal}
                title={t(addButtonKey)}
              >
                <i className="zmdi zmdi-plus"></i>
              </button>
            </RoleBasedAccess>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home m-r-5"></i> {t('COMMON_HOME')}
                </Link>
              </li>
              <li className="breadcrumb-item active">{t(titleKey)}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12 date-scenario-filter-container">
            <FilterCard
              filters={filters}
              onFilterChange={handleFilterChange}
              onFilterSubmit={handleFilterSubmit}
              onClearFilters={handleClearFilters}
              onRefresh={() =>
                filters.universityId &&
                (!requiresProgramSelection || filters.programId) &&
                fetchRecords({
                  universityId: filters.universityId,
                  programId: filters.programId,
                  page: supportsServerPagination ? serverPagination.page : undefined,
                  limit: supportsServerPagination ? serverPagination.limit : undefined,
                })
              }
              loading={loading}
              title="DATE_SCENARIO_FILTER_TITLE"
              titleHide="DATE_SCENARIO_FILTER_HIDE"
              titleShow="DATE_SCENARIO_FILTER_SHOW"
              fields={filterFields}
              defaultFilters={filterDefaults}
              autoSubmitFields={autoSubmitFieldNames}
              topContent={
                <>
                  {renderHeaderTabs({ variant: 'card' })}
                  {usageInfoKey && (
                    <div className="mt-3 text-muted text-left d-flex align-items-center" style={{ fontSize: '11px' }}>
                      <i className="zmdi zmdi-info-outline mr-1"></i>
                      <span>{t(usageInfoKey)}</span>
                    </div>
                  )}
                </>
              }
            />
          </div>

          <div className="col-lg-12">
            {!filters.universityId && !filters.cityId && !globalApiUrl ? (
              <div className="card">
                <div className="body text-center">
                  <i className="zmdi zmdi-info-outline m-b-10" style={{ fontSize: '32px' }}></i>
                  <p>{t('DATE_SCENARIO_SELECT_UNIVERSITY')}</p>
                </div>
              </div>
            ) : requiresProgramSelection && !filters.programId ? (
              <div className="card">
                <div className="body text-center">
                  <i className="zmdi zmdi-info-outline m-b-10" style={{ fontSize: '32px' }}></i>
                  <p>{t('DATE_SCENARIO_SELECT_PROGRAM')}</p>
                </div>
              </div>
            ) : usesSimpleTable ? (
              <>
                {(isRegistrationScenario || isPreferenceDatesScenario) ? (
                  <div className="row clearfix">
                    {loading ? (
                      <div className="col-12 text-center p-5">
                        <i className="zmdi zmdi-spinner zmdi-hc-spin" style={{ fontSize: '40px' }}></i>
                      </div>
                    ) : simpleTableData.length === 0 ? (
                      <div className="col-lg-12">
                        <div className="card">
                          <div className="body text-center">
                            <p>{t(emptyStateKey)}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      simpleTableData.map((item, index) => (
                        <PreferenceDateCard
                          key={item.id || index}
                          data={item}
                          onEdit={openEditModal}
                          onDelete={handleDelete}
                          onView={openDetailModal}
                        />
                      ))
                    )}
                  </div>
                ) : (
                  <Table
                    columns={tableColumns}
                    data={simpleTableData}
                    loading={loading}
                    emptyMessage={t(emptyStateKey)}
                    renderActions={renderSimpleTableActions}
                    showAutoId={false}
                    pagination={
                      supportsServerPagination
                        ? { page: serverPagination.page, limit: serverPagination.limit }
                        : isSimplePaginationEnabled && simplePaginationMeta
                          ? { page: simplePaginationMeta.currentPage, limit: simplePagination.limit }
                          : undefined
                    }
                  />
                )}
                {supportsServerPagination && serverPagination.total > 0 ? (
                  <Pagination
                    currentPage={serverPagination.page}
                    totalPages={serverPagination.totalPages}
                    total={serverPagination.total}
                    limit={serverPagination.limit}
                    onPageChange={handleServerPageChange}
                    onLimitChange={handleServerLimitChange}
                  />
                ) : (
                  isSimplePaginationEnabled &&
                  simplePaginationMeta &&
                  simplePaginationMeta.totalItems > 0 && (
                    <Pagination
                      currentPage={simplePaginationMeta.currentPage}
                      totalPages={simplePaginationMeta.totalPages}
                      total={simplePaginationMeta.totalItems}
                      limit={simplePagination.limit}
                      onPageChange={handleSimplePageChange}
                      onLimitChange={handleSimpleLimitChange}
                    />
                  )
                )}
              </>
            ) : (
              <DataTable
                columns={tableColumns}
                data={filteredRecords}
                loading={loading}
                emptyMessage={t(emptyStateKey)}
                emptyIcon="zmdi-calendar-close"
                itemLabel={t(entityNameKey)}
              />
            )}
          </div>
        </div>
      </div>

      <RoleBasedAccess allowedRoles={[UserTypeEnum.ADMIN]}>
        <DateScenarioModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
          initialValues={modalInitialValues || undefined}
          isEditMode={!!editingRecord}
          universityName={selectedUniversity?.name || selectedUniversity?.title || ''}
          programs={modalPrograms}
          programsLoading={modalProgramsLoading}
          universities={universities}
          allowUniversitySelection={!editingRecord}
          onUniversityChange={handleModalUniversityChange}
          scenarioType={scenarioType}
        />
      </RoleBasedAccess>
      {usesSimpleTable && (
        <DateScenarioDetailModal
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          record={detailRecord}
          scenarioType={scenarioType}
        />
      )}
    </section>
  );
};

export default DateScenarioPage;

